import { Link } from 'react-router-dom';
import { 
  CreditCard, FileText, User, Scale, Search, 
  Clock, CheckCircle, ArrowRight, Shield, Users, TrendingUp,
  Download, ExternalLink, FileCheck, Briefcase, Heart, MapPin, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Layout } from '../components/layout/Layout';

export default function Services() {
  const services = [
    {
      icon: CreditCard,
      title: 'Charge-Off Removal',
      description: 'We assist with the removal of inaccurately reported charge-offs from your credit report.',
      items: [
        'Credit Card Charge-Offs',
        'Auto Loan Charge-Offs (including repossessions)',
        'Personal Loan Charge-Offs',
      ],
      timeline: '3-6 months based on complexity',
    },
    {
      icon: FileText,
      title: 'Collection Disputes',
      description: 'We challenge collections that are inaccurate, incomplete, or unverifiable.',
      items: [
        'Medical Collections',
        'Credit Card Collections',
        'Auto Loan Collections',
        'Utility Bill Collections',
        'Student Loan Collections',
        'Personal Loan Collections',
        'Apartment Debt Collections',
      ],
      timeline: 'Up to 90 days depending on type',
    },
    {
      icon: User,
      title: 'Personal Information Corrections',
      description: 'We correct or remove inaccurate personal information that may affect your credit profile.',
      items: [
        'Incorrect names or aliases',
        'Outdated addresses',
        'Wrong phone numbers',
        'Inaccurate employment listings',
      ],
      timeline: '30-60 days typically',
    },
    {
      icon: Scale,
      title: 'Public Records',
      description: 'We dispute inaccurately reported public records under FCRA and Metro 2® compliance.',
      items: [
        'Improperly reported bankruptcies',
        'Incorrect filing dates or discharge dates',
        'Wrong chapter types',
        'Inaccurately reported accounts in bankruptcy',
      ],
      timeline: 'Up to 6 months based on complexity',
    },
    {
      icon: Search,
      title: 'Credit Inquiry Disputes',
      description: 'We help remove inquiries that shouldn\'t be on your credit report.',
      items: [
        'Unauthorized hard inquiries',
        'Duplicate inquiries',
        'Inquiries without permissible purpose',
      ],
      timeline: '30-45 days typically',
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-slate-50" data-testid="services-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <p className="text-amber-700 font-medium">Our Services</p>
              <span className="bg-teal-600 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Serving All 50 States
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Credit Items We Work to Remove or Correct
            </h1>
            <p className="text-lg text-slate-600">
              We focus on challenging inaccurate, outdated, incomplete, or unverifiable 
              information on your credit report in compliance with the Fair Credit Reporting 
              Act (FCRA) and Metro 2® reporting standards.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white" data-testid="services-grid">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {services.map((service, index) => (
              <Card key={index} className="overflow-hidden border-slate-200 card-hover">
                <div className="grid md:grid-cols-3 gap-0">
                  <div className="bg-slate-50 p-8 flex flex-col justify-center">
                    <div className="service-icon mb-4">
                      <service.icon className="h-8 w-8 text-amber-700" strokeWidth={1.5} />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                      {service.title}
                    </h2>
                    <p className="text-slate-600 text-sm mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-teal-700">
                      <Clock className="h-4 w-4" />
                      <span>Timeline: {service.timeline}</span>
                    </div>
                  </div>
                  <div className="md:col-span-2 p-8">
                    <h3 className="font-semibold text-slate-900 mb-4">What We Address:</h3>
                    <ul className="grid sm:grid-cols-2 gap-3">
                      {service.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-24 bg-slate-900 text-white" data-testid="compliance-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-500 font-medium mb-2">Our Standards</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Compliance & Legal Standards
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-white">
                  <Shield className="h-6 w-6 text-amber-500" />
                  FCRA Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="text-slate-300">
                <p className="mb-4">
                  We operate fully in compliance with the Fair Credit Reporting Act (FCRA), ensuring:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 mt-1" />
                    <span>Accuracy of reported credit data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 mt-1" />
                    <span>Consumer consent before accessing credit information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 mt-1" />
                    <span>Client privacy protection at all times</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 mt-1" />
                    <span>Your right to dispute inaccurate information</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-white">
                  <Shield className="h-6 w-6 text-amber-500" />
                  Metro 2® Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="text-slate-300">
                <p className="mb-4">
                  We utilize the Metro 2® reporting format, the industry standard for reporting to major bureaus:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 mt-1" />
                    <span>Targets factual errors in credit data reporting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 mt-1" />
                    <span>Strengthens disputes based on Metro 2® errors</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 mt-1" />
                    <span>Encourages correction of non-compliant data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-400 mt-1" />
                    <span>Supports long-term credit improvement</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Digital Products Section */}
      <section className="py-24 bg-white" data-testid="digital-products">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-amber-700 font-medium mb-2">DIY Resources</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Digital Products & Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Take control of your Credit Restoration journey with our DIY resources and professional guidance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* 1. Personal Information Deletion Letter - FREE */}
            <Card className="border-teal-200 card-hover bg-teal-50/30">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="service-icon">
                    <User className="h-6 w-6 text-teal-700" strokeWidth={1.5} />
                  </div>
                  <span className="text-2xl font-bold text-teal-700">FREE</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  Personal Information Deletion Letter
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Template to correct or remove inaccurate personal information from your credit reports.
                </p>
                <a 
                  href="https://customer-assets.emergentagent.com/job_tamperis-restore/artifacts/hg3ekhr2_Personal%20Information%20Update%20Letter.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-teal-700 text-sm font-medium hover:text-teal-800"
                  data-testid="personal-letter-download"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download Free
                </a>
              </CardContent>
            </Card>

            {/* 2. Steps to Start Business - FREE */}
            <Card className="border-teal-200 card-hover bg-teal-50/30">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="service-icon">
                    <TrendingUp className="h-6 w-6 text-teal-700" strokeWidth={1.5} />
                  </div>
                  <span className="text-2xl font-bold text-teal-700">FREE</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  Steps to Start Your Business
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Comprehensive guide on how to start your own credit restoration business from scratch.
                </p>
                <a 
                  href="https://customer-assets.emergentagent.com/job_tamperis-restore/artifacts/h125m8px_Step%20by%20Step%20on%20how%20to%20Start%20A%20business.docx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-teal-700 text-sm font-medium hover:text-teal-800"
                  data-testid="business-guide-download"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download Free
                </a>
              </CardContent>
            </Card>

            {/* 3. Debt Validation Letter - $5 */}
            <a 
              href="https://buy.stripe.com/9B69ASenc31M1Epcuvco00N" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
              data-testid="debt-validation-link"
            >
              <Card className="border-slate-200 card-hover h-full cursor-pointer hover:border-amber-300 transition-colors">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="service-icon">
                      <FileCheck className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
                    </div>
                    <span className="text-2xl font-bold text-slate-900">$5</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    Debt Validation Letter
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Professionally crafted letter to request debt validation from creditors and collectors.
                  </p>
                  <div className="flex items-center text-amber-700 text-sm font-medium">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Purchase Now
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* 4. Inquiry Removal Letter - $10 */}
            <a 
              href="https://buy.stripe.com/00w00i1Aq45Qcj3cuvco00J" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
              data-testid="inquiry-letter-link"
            >
              <Card className="border-slate-200 card-hover h-full cursor-pointer hover:border-amber-300 transition-colors">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="service-icon">
                      <Search className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
                    </div>
                    <span className="text-2xl font-bold text-slate-900">$10</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    Inquiry Removal Letter
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Professional template to dispute unauthorized or duplicate credit inquiries.
                  </p>
                  <div className="flex items-center text-amber-700 text-sm font-medium">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Purchase Now
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* 5. Collection Removal Letter - $20 */}
            <a 
              href="https://buy.stripe.com/aFabJ0enc0TEerb667co016" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
              data-testid="collection-letter-link"
            >
              <Card className="border-slate-200 card-hover h-full cursor-pointer hover:border-amber-300 transition-colors">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="service-icon">
                      <FileText className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
                    </div>
                    <span className="text-2xl font-bold text-slate-900">$10</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    Collection Removal Letter
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Professional template to dispute and remove collection accounts from your credit report.
                  </p>
                  <div className="flex items-center text-amber-700 text-sm font-medium">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Purchase Now
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* 6. Medical Collection Letter - $20 */}
            <a 
              href="https://buy.stripe.com/aFabJ0enc0TEerb667co016" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
              data-testid="medical-collection-link"
            >
              <Card className="border-slate-200 card-hover h-full cursor-pointer hover:border-amber-300 transition-colors">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="service-icon">
                      <Heart className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
                    </div>
                    <span className="text-2xl font-bold text-slate-900">$10</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    Medical Collection Removal Letter
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Specialized template to dispute and remove medical collection accounts from your credit report.
                  </p>
                  <div className="flex items-center text-amber-700 text-sm font-medium">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Purchase Now
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* 7. Resume & Cover Letter - $50 */}
            <a 
              href="https://buy.stripe.com/8x2cN4gvk6dYaaVbqrco00M" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
              data-testid="resume-cover-link"
            >
              <Card className="border-slate-200 card-hover h-full cursor-pointer hover:border-amber-300 transition-colors">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="service-icon">
                      <Briefcase className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
                    </div>
                    <span className="text-2xl font-bold text-slate-900">$50</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    Resume & Cover Letter
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Professionally crafted resume and cover letter with up to 3 revisions included.
                  </p>
                  <div className="flex items-center text-amber-700 text-sm font-medium">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Purchase Now
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* 9. Website Creation - $500 */}
            <a 
              href="https://buy.stripe.com/4gM3cu5QG45Q4QB8efco00K" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
              data-testid="website-creation-link"
            >
              <Card className="border-slate-200 card-hover h-full cursor-pointer hover:border-amber-300 transition-colors">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="service-icon">
                      <Scale className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
                    </div>
                    <span className="text-2xl font-bold text-slate-900">$500</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    Professional Website Creation
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Get a custom, professional website built for your business to establish your online presence.
                  </p>
                  <div className="flex items-center text-amber-700 text-sm font-medium">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Purchase Now
                  </div>
                </CardContent>
              </Card>
            </a>

            {/* Exclusive 1-on-1 Coaching */}
            <Card className="border-amber-300 card-hover bg-gradient-to-br from-amber-50 to-white md:col-span-2 lg:col-span-3 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-100 rounded-full -translate-y-32 translate-x-32 opacity-50"></div>
              <CardContent className="p-8 relative">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <Users className="h-10 w-10 text-white" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="bg-amber-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        EXCLUSIVE
                      </span>
                      <span className="bg-slate-900 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        PREMIUM
                      </span>
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                      1-on-1 Business & Credit Coaching
                    </h3>
                    <p className="text-slate-600 mb-6">
                      A 3-hour personalized coaching session designed to transform your financial future and business success.
                    </p>
                    
                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-slate-900">Start Any Business</p>
                          <p className="text-sm text-slate-600">Step-by-step guidance on launching your business from scratch</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-slate-900">Build & Structure Credit</p>
                          <p className="text-sm text-slate-600">Create a credit profile to access funding and scale your business</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-slate-900">Tradeline Vendor Access</p>
                          <p className="text-sm text-slate-600">Get connected to trusted tradeline vendors to boost your credit</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-slate-900">Virtual Assistance</p>
                          <p className="text-sm text-slate-600">Learn how to automate your business with virtual assistants</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center lg:items-end gap-4 lg:min-w-[220px]">
                    <div className="text-center lg:text-right">
                      <p className="text-sm text-slate-500 mb-1">Investment</p>
                      <span className="text-4xl font-bold text-amber-700">$1,500</span>
                      <p className="text-sm text-slate-600 mt-2">One Day Session:</p>
                      <p className="text-xs text-slate-500">3 hours total</p>
                      <p className="text-xs text-teal-600 font-semibold mt-2">FULL ACCESS TO ME</p>
                      <p className="text-xs text-slate-500 mt-1">Recording included</p>
                      <p className="text-xs text-slate-500">Resources sent to your email</p>
                    </div>
                    <div className="bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1 rounded-full">
                      Coming Soon
                    </div>
                    <Button className="bg-amber-600 hover:bg-amber-700 shadow-lg px-8">
                      Join the Wait List
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-50" data-testid="services-cta">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Ready to Start Your Credit Restoration Journey?
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Our team is ready to help you dispute inaccurate information and work toward 
            restoring your credit. Choose the package that fits your needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/pricing#packages">
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800 btn-active">
                Choose Your Package
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a 
              href="https://calendly.com/tamperis94/30min" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 gap-2" data-testid="services-book-call-btn">
                <Calendar className="h-5 w-5" />
                Book a Free Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
